/**
 * Internal dependencies
 */
import { setParentState } from '.';
import { getCssPath } from './css-path';
import { getElementLabel } from './element-label';

export type SelectorBreadcrumb = { label: string; value: string };
export function getBreadcrumbs(
	oldBreadcrumbs: ReadonlyArray< SelectorBreadcrumb >,
	selector: string
): ReadonlyArray< SelectorBreadcrumb > {
	if (
		oldBreadcrumbs
			.map( ( b ) => b.value )
			.some( compareSelectors( selector ) )
	) {
		return oldBreadcrumbs;
	}

	const element = querySelector( selector );
	if ( ! element ) {
		return [];
	}

	const breadcrumbs = doGetBreadcrumbs( selector );
	if ( ! startsWith( oldBreadcrumbs, breadcrumbs ) ) {
		return breadcrumbs;
	}

	return [ ...breadcrumbs, ...oldBreadcrumbs.slice( breadcrumbs.length ) ];
}

export function renderBreadcrumbs(
	breadcrumbs: ReadonlyArray< SelectorBreadcrumb >,
	activeSelector: string
): void {
	let container = querySelector( '.nab-breadcrumbs-container' );
	if ( ! container ) {
		container = document.createElement( 'div' );
		container.classList.add( 'nab-breadcrumbs-container' );
		document.body.appendChild( container );
	}

	container.innerHTML = '';

	const list = document.createElement( 'ul' );
	list.classList.add( 'nab-breadcrumbs-list' );
	container.appendChild( list );

	const isActiveElement = compareSelectors( activeSelector );
	breadcrumbs.forEach( ( breadcrumb ) => {
		const li = document.createElement( 'li' );
		li.classList.add( 'nab-breadcrumbs-list-item' );
		if ( isActiveElement( breadcrumb.value ) ) {
			li.classList.add( 'nab-breadcrumbs-list-item--selected' );
		} else {
			li.addEventListener( 'click', ( ev ) => {
				ev.preventDefault();
				clearBreadcrumbHighlighters();
				const exists = !! querySelector( breadcrumb.value );
				setParentState( {
					value: exists ? breadcrumb.value : '',
					breadcrumbs: exists ? breadcrumbs : [],
				} );
			} );
			li.addEventListener( 'mouseenter', ( ev ) => {
				ev.preventDefault();
				renderBreadcrumbHighlighters( breadcrumb.value );
			} );
			li.addEventListener( 'mouseleave', ( ev ) => {
				ev.preventDefault();
				clearBreadcrumbHighlighters();
			} );
		}
		li.setAttribute( 'data-nab-selector', breadcrumb.value );
		const span = document.createElement( 'span' );
		span.classList.add( 'nab-breadcrumbs-list-item__content' );
		span.textContent = breadcrumb.label;
		li.appendChild( span );
		list.appendChild( li );
	} );
}

export function isBreadcrumb( element: HTMLElement ): boolean {
	return (
		element.classList.value.includes( 'nab-breadcrumbs-container' ) ||
		element.classList.value.includes( 'nab-breadcrumbs-list' ) ||
		element.classList.value.includes( 'nab-breadcrumbs-list-item' )
	);
}

// =======
// HELPERS
// =======

const compareSelectors = ( selector1: string ) => {
	const firstElements = querySelectorAll( selector1 );
	return ( selector2: string ): boolean => {
		if ( selector1 === selector2 ) {
			return true;
		}
		if ( ! selector1.length ) {
			return false;
		}
		const secondElements = querySelectorAll( selector2 );
		return (
			firstElements.length === secondElements.length &&
			firstElements.reduce(
				( r, e ) => r && secondElements.includes( e ),
				true
			)
		);
	};
};

function doGetBreadcrumbs( selector: string ) {
	const matchingElements = querySelectorAll( selector );
	const element = matchingElements[ 0 ];
	if ( ! element ) {
		return [];
	}

	const breadcrumbs: Array< SelectorBreadcrumb > = [
		mkBreadcrumb( element, selector ),
	];

	let currentElement = element.parentElement;
	while ( currentElement ) {
		const single = getCssPath( currentElement ) || '';
		const singleElement = querySelector( single );
		if ( containsAll( singleElement, matchingElements ) ) {
			breadcrumbs.push( mkBreadcrumb( currentElement, single ) );
		}
		currentElement = currentElement.parentElement;
	}

	return breadcrumbs.reverse();
}

let breadcrumbHighlightBoxes = [] as ReadonlyArray< HTMLElement >;
function clearBreadcrumbHighlighters(): void {
	if ( 0 === breadcrumbHighlightBoxes.length ) {
		return;
	}

	breadcrumbHighlightBoxes.forEach(
		( breadcrumbHighlighter ) =>
			breadcrumbHighlighter.parentElement &&
			breadcrumbHighlighter.parentElement.removeChild(
				breadcrumbHighlighter
			)
	);

	breadcrumbHighlightBoxes = [];
}

function renderBreadcrumbHighlighters( selector: string ) {
	clearBreadcrumbHighlighters();

	const elements = querySelectorAll( selector );
	breadcrumbHighlightBoxes = elements
		.map( ( element ) => createBreadcrumbHighlightedBox( element ) )
		.filter( ( e ) => !! e );
}

function createBreadcrumbHighlightedBox(
	element: HTMLElement
): HTMLElement | undefined {
	const rect = element.getBoundingClientRect();
	if ( rect.width === 0 || rect.height === 0 ) {
		return;
	}

	const highlighter = document.createElement( 'div' );
	highlighter.className = 'nab-breadcrumb-highlight';
	document.body.appendChild( highlighter );

	positionElementBox( highlighter, rect );
	return highlighter;
}

function positionElementBox( box: HTMLElement, rect: DOMRect ) {
	if ( ! box.style ) {
		return;
	}

	box.style.display = 'block';
	box.style.top = `${ Math.floor( rect.top ) - 1 + window.scrollY }px`;
	box.style.left = `${ Math.floor( rect.left ) - 1 + window.scrollX }px`;
	box.style.width = `${ rect.width }px`;
	box.style.height = `${ rect.height }px`;
}

function startsWith(
	list: ReadonlyArray< SelectorBreadcrumb >,
	init: ReadonlyArray< SelectorBreadcrumb >
) {
	return init.every( ( b, idx ) =>
		compareSelectors( b.value )( list[ idx ]?.value ?? '' )
	);
}

function containsAll(
	container: Element | null,
	children: ReadonlyArray< Element >
) {
	return !! container && children.every( ( c ) => container.contains( c ) );
}

function mkBreadcrumb( element: HTMLElement, selector: string ) {
	return {
		label: getElementLabel( getCssPath( element ) || selector ),
		value: selector,
	};
}

function querySelector( selector: string ) {
	try {
		return document.querySelector< HTMLElement >( selector );
	} catch ( _ ) {
		return null;
	}
}

function querySelectorAll( selector: string ) {
	try {
		return Array.from(
			document.querySelectorAll< HTMLElement >( selector )
		);
	} catch ( _ ) {
		return [];
	}
}
